import { Big } from 'hypo-shared-utils';
import {
  isPercentageInRange,
  greaterThanOrEqual,
  getFeeChoiceLabelKey,
  equal,
  isMaxFee,
} from '../../utils/utils';
import { FEE_TYPES } from '../../utils/constants';

const { MAX_OR_FEE_SCHEDULE, FLAT, VARIES } = FEE_TYPES;

export const getMaxFeeRange = advisoryFeeSchedule => {
  if (!advisoryFeeSchedule) {
    return null;
  }
  const { annualFeeNoLessThan, maxFee } = advisoryFeeSchedule;
  return { min: annualFeeNoLessThan || 0, max: maxFee };
};

export const getHistoricMaxFeeRange = advisoryFeeSchedule => {
  const scheduleDetails = advisoryFeeSchedule?.feeScheduleDetails;
  if (!scheduleDetails || scheduleDetails?.length === 0) {
    return null;
  }
  const { annualFeeNoLessThan } = advisoryFeeSchedule;
  const maxFees = scheduleDetails.map(timeSchedule => {
    const { maxFee } = timeSchedule;
    return parseFloat(maxFee);
  });
  return { min: annualFeeNoLessThan || 0, max: Math.max(...maxFees) };
};

export const getDiscountRange = advisoryFeeSchedule => {
  if (!advisoryFeeSchedule) {
    return null;
  }
  const { maxDiscount, allowMaxDiscount } = advisoryFeeSchedule;
  return { min: 0, max: allowMaxDiscount ? maxDiscount : 0 };
};

const getAlertDataForMaxfee = (advisoryFeeSchedule, thisObj) => {
  const data = isMaxFee(advisoryFeeSchedule)
    ? getMaxFeeRange(advisoryFeeSchedule)
    : getHistoricMaxFeeRange(advisoryFeeSchedule);
  return {
    title: thisObj.translate('invalidMaxFeeAlertTitle'),
    text: thisObj.translate('invalidMaxFeeAlertMsg', {
      min: thisObj.formatter.number(data.min || 0, 'longDecimal'),
      max: thisObj.formatter.number(data.max, 'longDecimal'),
    }),
  };
};

const getAlertData = (advisoryFeeSchedule, fieldName, thisObj) => {
  if (fieldName === 'annualFeeAmount') {
    return getAlertDataForMaxfee(advisoryFeeSchedule, thisObj);
  }
  if (fieldName === 'discount') {
    return {
      title: thisObj.translate('invalidDiscountAlertTitle'),
      text: thisObj.translate('invalidDiscountAlertMsg', {
        min: thisObj.formatter.number(0, 'longDecimal'),
        max: thisObj.formatter.number(advisoryFeeSchedule?.maxDiscount, 'longDecimal'),
      }),
    };
  }
  return null;
};

export const triggerAdvisoryFeeAlert = (advisoryFeeSchedule, fieldName, thisObj) => {
  const alertData = getAlertData(advisoryFeeSchedule, fieldName, thisObj);
  thisObj.$emit('alert-triggered', alertData);
};

export function calcAnnualMaxFee(maxFee, discount) {
  if (isPercentageInRange(discount) && !equal(discount, 0)) {
    return Big(100)
      .minus(discount)
      .div(100)
      .times(maxFee)
      .round(4)
      .toNumber();
  }
  return maxFee;
}

export function calcDiscount(maxFee, annualMaxFee) {
  if (greaterThanOrEqual(maxFee, annualMaxFee)) {
    return Big(maxFee)
      .minus(annualMaxFee)
      .div(maxFee)
      .times(100)
      .round(4)
      .toNumber();
  }
  return 0;
}

const annualFeeTypeOptionsMapping = {
  maxFeeAndFeeSchedule: {
    labelKey: 'maxFeeAndFeeSchedule',
    holdingLabelKey: 'maxSchedule',
    value: MAX_OR_FEE_SCHEDULE,
  },
  annualMaxFeeAndFeeSchedule: {
    labelKey: 'annualMaxFeeAndFeeSchedule',
    holdingLabelKey: 'maxSchedule',
    value: MAX_OR_FEE_SCHEDULE,
  },
  maxFee: { labelKey: 'maxFee', holdingLabelKey: 'max', value: MAX_OR_FEE_SCHEDULE },
  feeSchedule: {
    labelKey: 'feeSchedule',
    holdingLabelKey: 'schedule',
    value: MAX_OR_FEE_SCHEDULE,
  },
  flat: { labelKey: 'flat', holdingLabelKey: 'percent', value: FLAT },
  variesAcrossHoldings: {
    labelKey: 'variesAcrossHoldings',
    holdingLabelKey: '',
    value: VARIES,
  },
};

export const getAnnualFeeTypeOptions = (
  { feeScheduleLabelType, assetBasedFeeConfigType },
  varies = false
) => {
  const feeChoiceLabelKey = getFeeChoiceLabelKey({ feeScheduleLabelType, assetBasedFeeConfigType });
  const {
    flat,
    variesAcrossHoldings,
    [feeChoiceLabelKey]: feeChoiceObj,
  } = annualFeeTypeOptionsMapping;
  const options = [feeChoiceObj, flat];
  if (varies) {
    options.push(variesAcrossHoldings);
  }
  return options;
};

export const isHistoricManagerFee = historicManagerFee => {
  return (
    Array.isArray(historicManagerFee) &&
    historicManagerFee.length > 1 &&
    historicManagerFee.some(
      timeSchedule => timeSchedule?.managerFee !== historicManagerFee?.[0]?.managerFee
    )
  );
};

export const isPrivateModel = securityType => ['PM'].includes(securityType);

export const isSeparateAccount = securityType => ['SA'].includes(securityType);

export const getAdvisoryFeeSchedule = (feeScheduleId, allFeeScheduleList) => {
  if (feeScheduleId) {
    const advisoryFeeSchedule = allFeeScheduleList.find(
      feeSchedule => feeSchedule.feeScheduleId === feeScheduleId
    );
    return advisoryFeeSchedule || null;
  }
  return null;
};

const checkWithinRange = (value, max, min) => {
  const almostEqual = Math.abs(value - min) < 0.00001 || Math.abs(max - value) < 0.00001;
  const withinRange = value >= min && value <= max;
  return typeof value !== 'undefined' && (almostEqual || withinRange);
};

export const getUpdatedMaxFeeAndDiscountWhenFeeScheduleChange = (
  fees,
  advisoryFeeSchedule,
  defaultDiscount
) => {
  if (!advisoryFeeSchedule) {
    return {};
  }
  // Security does contain feeScheduleId and corresponding feeSchedule hasn't been deleted
  const { annualMaxFee, discount = defaultDiscount } = fees;
  let newAnnualMaxFee;
  let newDiscount;
  const { annualFeeNoLessThan, maxFee, allowMaxDiscount } = advisoryFeeSchedule;
  if (isMaxFee(advisoryFeeSchedule)) {
    if (allowMaxDiscount && checkWithinRange(annualMaxFee, maxFee, annualFeeNoLessThan)) {
      // Old annualMaxFee data is still valid with latest maxFeeSchedue
      newAnnualMaxFee = annualMaxFee;
      newDiscount = calcDiscount(maxFee, annualMaxFee);
    } else {
      newAnnualMaxFee = maxFee;
      newDiscount = defaultDiscount;
    }
  } else {
    // Security has not-maxfee feeSchdule
    // eslint-disable-next-line no-lonely-if
    if (allowMaxDiscount) {
      // 1.update fee schedule discount.
      // 2.same fee schedule, but max discount or allow discount may be updated.
      newDiscount = Math.min(discount, advisoryFeeSchedule.maxDiscount);
    }
  }
  return {
    annualMaxFee: newAnnualMaxFee,
    discount: newDiscount,
  };
};

export const setAdvisoryFeeDataForSecurity = (
  security,
  newFeeScheduleId,
  allFeeScheduleList,
  defaultDiscount
) => {
  const {
    annualFeeAmountType = 1,
    annualFeeAmount,
    annualFeeChargeFrequency,
    annualFeeType,
    feeScheduleId = '',
    annualMaxFee,
    discount = 0,
    ...otherFees
  } = security.fees ?? {};
  const advisoryFeeSchedule = getAdvisoryFeeSchedule(newFeeScheduleId, allFeeScheduleList);
  const isFlatFee = annualFeeType === FLAT;
  const isMaxOrFeeSchedule = annualFeeType === MAX_OR_FEE_SCHEDULE;

  let newFees = {
    annualFeeType,
    annualFeeChargeFrequency,
    ...otherFees,
    ...(isFlatFee
      ? { annualFeeAmountType, annualFeeAmount }
      : { feeScheduleId, annualMaxFee, discount }),
  };

  if (!isFlatFee) {
    const updatedFeeScheduleId = isMaxOrFeeSchedule && advisoryFeeSchedule ? newFeeScheduleId : '';
    const newMaxFeeAndDiscount = getUpdatedMaxFeeAndDiscountWhenFeeScheduleChange(
      newFees,
      advisoryFeeSchedule,
      defaultDiscount
    );
    newFees = {
      ...newFees,
      feeScheduleId: updatedFeeScheduleId,
      ...newMaxFeeAndDiscount,
    };
  }
  return { ...security, fees: newFees, advisoryFeeSchedule };
};
